import { z } from 'zod';
/**
 * Runtime configuration of a shell (ADR *runtime-config-from-the-edge*).
 *
 * A production build is ONE artifact promoted dev → test → prod; what differs per cell × env
 * (the Cognito pool, the client id, the cell name) is served by the edge as `/config.json`,
 * written by the edge context from SSM at deploy time. Nothing in it is secret: it is public
 * by construction, exactly like a `VITE_` variable would be.
 *
 * In `vite` dev the same shape is built from `VITE_*` variables (default: mock mode).
 */
export const CognitoAuthConfigSchema = z.object({
    mode: z.literal('cognito'),
    region: z.string().regex(/^[a-z]{2}-[a-z]+-\d$/, 'an AWS region'),
    userPoolId: z
        .string()
        .regex(/^[a-z]{2}-[a-z]+-\d_[A-Za-z0-9]+$/, 'a Cognito user pool id like us-east-1_AbCdEfGhI'),
    clientId: z.string().min(1),
});
export const MockAuthConfigSchema = z.object({ mode: z.literal('mock') });
export const AppConfigSchema = z.object({
    /** Bumped when the shape changes; a mismatch is a configuration error, never a guess. */
    version: z.literal(1),
    app: z.enum(['web', 'admin']),
    cell: z.string().min(1),
    env: z.enum(['dev', 'test', 'prod', 'local']),
    appName: z.string().min(1).default('Sprocket'),
    auth: z.discriminatedUnion('mode', [MockAuthConfigSchema, CognitoAuthConfigSchema]),
    api: z
        .object({
        basePath: z
            .string()
            .regex(/^\/[a-z0-9/-]*$/)
            .default('/api'),
    })
        .default({
        basePath: '/api',
    }),
    /**
     * Which control-plane surfaces this edge proxies. `signup` is false while the edge has no
     * `/api/platform/*` behaviour (control-plane origin not configured yet): the sign-up page
     * then says so instead of failing on a 404.
     */
    controlPlane: z
        .object({ platform: z.boolean().default(false), billing: z.boolean().default(false) })
        .default({ platform: false, billing: false }),
});
/** The URL the edge serves the runtime configuration at. */
export const CONFIG_PATH = '/config.json';
/** A page may inline its configuration (tests, previews) instead of fetching it. */
export const CONFIG_GLOBAL = '__PLATFORM_CONFIG__';
const VITE_KEYS = [
    'VITE_AUTH_MODE',
    'VITE_COGNITO_REGION',
    'VITE_COGNITO_CLIENT_ID',
    'VITE_COGNITO_USER_POOL_ID',
    'VITE_APP_NAME',
    'VITE_CELL',
    'VITE_ENV',
    'VITE_CONTROL_PLANE',
];
function problemsOf(error) {
    return error.issues.map((i) => `${i.path.join('.') || 'config'}: ${i.message}`);
}
/**
 * Build a configuration from `import.meta.env` (`vite` dev, or an explicit build-time mode).
 * Returns `null` when no `VITE_AUTH_MODE` is set — the caller then fetches `/config.json`.
 * In `vite` dev an unset mode means `mock`; a build never defaults.
 */
export function configFromEnv(app, raw) {
    const isDev = raw['DEV'] === true;
    const v = {};
    for (const key of VITE_KEYS) {
        const value = raw[key];
        if (typeof value === 'string' && value.length > 0)
            v[key] = value;
    }
    const mode = v.VITE_AUTH_MODE ?? (isDev ? 'mock' : undefined);
    if (!mode)
        return null;
    const auth = mode === 'cognito'
        ? {
            mode: 'cognito',
            region: v.VITE_COGNITO_REGION ?? regionOfPool(v.VITE_COGNITO_USER_POOL_ID),
            userPoolId: v.VITE_COGNITO_USER_POOL_ID,
            clientId: v.VITE_COGNITO_CLIENT_ID,
        }
        : { mode };
    const controlPlane = v.VITE_CONTROL_PLANE
        ? {
            platform: v.VITE_CONTROL_PLANE.includes('platform'),
            billing: v.VITE_CONTROL_PLANE.includes('billing'),
        }
        : mode === 'mock'
            ? { platform: true, billing: true }
            : undefined;
    const parsed = AppConfigSchema.safeParse({
        version: 1,
        app,
        cell: v.VITE_CELL ?? 'local',
        env: v.VITE_ENV ?? 'local',
        ...(v.VITE_APP_NAME ? { appName: v.VITE_APP_NAME } : {}),
        auth,
        ...(controlPlane ? { controlPlane } : {}),
    });
    if (parsed.success)
        return { ok: true, config: parsed.data };
    return { ok: false, problems: problemsOf(parsed.error) };
}
function regionOfPool(userPoolId) {
    const region = userPoolId?.split('_')[0];
    return region && region.length > 0 ? region : undefined;
}
/** Parse a configuration object served by the edge (or inlined on `window`). */
export function parseConfig(app, value) {
    const parsed = AppConfigSchema.safeParse(value);
    if (!parsed.success)
        return { ok: false, problems: problemsOf(parsed.error) };
    if (parsed.data.app !== app) {
        return {
            ok: false,
            problems: [
                `config: this is the ${parsed.data.app} configuration, but this is the ${app} app`,
            ],
        };
    }
    return { ok: true, config: parsed.data };
}
/**
 * Resolve the configuration, in this order: an explicit build-time/dev `VITE_AUTH_MODE`
 * (never defaulted in a build), an inlined `window.__PLATFORM_CONFIG__`, then `/config.json`
 * from the edge. A missing or invalid configuration is a hard error screen, never a fallback
 * to fixtures (ADR-0012).
 */
export async function loadAppConfig(options) {
    const fromEnv = configFromEnv(options.app, options.env);
    if (fromEnv)
        return fromEnv;
    const scope = options.globalScope ?? globalThis;
    const inlined = scope[CONFIG_GLOBAL];
    if (inlined !== undefined)
        return parseConfig(options.app, inlined);
    const fetchImpl = options.fetchImpl ?? globalThis.fetch;
    try {
        const response = await fetchImpl(CONFIG_PATH, { cache: 'no-store' });
        if (!response.ok) {
            return {
                ok: false,
                problems: [
                    `${CONFIG_PATH}: HTTP ${response.status} — the edge did not serve a configuration`,
                ],
            };
        }
        return parseConfig(options.app, await response.json());
    }
    catch (e) {
        return {
            ok: false,
            problems: [`${CONFIG_PATH}: ${e instanceof Error ? e.message : 'could not be loaded'}`],
        };
    }
}
//# sourceMappingURL=app-config.js.map